// CombinedMenuComponent.jsx

import { ChangeEvent, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { StyledLinkAnimation } from "../../../LinkAnimation.styles";
import CoaService from "../../../features/CoaView/services/CoaService";
import TemplateService from "../../../features/TemplateView/services/TemplateService";
import {
  hasExportTemplatePermission,
  hasImportTemplatePermission,
} from "../../../core/services/SessionStorageService";
import { ThemePalette } from "../../../theme";
import {
  EXPORT_BUTTON_LABEL,
  SAVE_TEXT,
  IMPORT_BUTTON_LABEL,
  NAME_KEY,
  TOAST_DEFAULT_ERROR_MESSAGE,
  EDIT_ONLINE_TEXT,
  ADDROW_TEXT,
  CANCEL_TEXT,
} from "../../utils/CommonConstants";
import {
  AdditionalTabs,
  ExportTab,
  SaveTab,
  ImportTab,
  StyledProjectMenu,
  EditOnlineTab,
  AddRowTab,
  CancelTab,
} from "../DeliverableMenuComponent/DeliverableMenuComponent.styles";
import {
  onHandleCoaExportClick,
  onHandleTemplateExportClick,
} from "../ExcelViewComponent/ExcelViewComponent.helper";
import { UserProfile } from "../../../shared/base-components/Navbar/UserProfile";
import { ListType } from "../../Enums/Enums";
import { message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useAgGridContext } from "../AgGridComponent/AgGridContext";

export const MenuComponent = ({
  onSaveClick,
  menuType,
}: {
  onSaveClick?: any;
  menuType: ListType;
}) => {
  const location = useLocation();
  const [templateName, setTemplateName] = useState<string | null>("");
  const coaService = new CoaService();
  const templateService = new TemplateService();
  const [searchParams] = useSearchParams();
  const [additionalTabsVisible, setAdditionalTabsVisible] =
    useState<boolean>(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [count, setCount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showAddNewRow, setShowAddNewRow] = useState<boolean>(false);

  const { deliverableId = "-1" } = useParams();
  const navigate = useNavigate();
  const { agGridRef } = useAgGridContext();
  useEffect(() => {
    setTemplateName(getTemplateName(location.search));
  }, [location.search]);

  const getTemplateName = (search: string): string => {
    const params = new URLSearchParams(search);
    return params.get(NAME_KEY) ?? "";
  };

  const handleImportButtonClick = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      try {
        if (menuType === ListType.Templates) {
          await templateService.importTemplate<any>(deliverableId, file);
          message.success("Template imported successfully");
        } else if (menuType === ListType.Coa) {
          await coaService.importCoa<any>(deliverableId, file);
          navigate(
            `/coa/${deliverableId}?${NAME_KEY}=${decodeURI(
              templateName ?? "",
            )}`,
            {
              state: { refreshPage: true },
            },
          );
          message.success("CoA imported successfully");
        }
      } catch (error) {
        if (menuType === ListType.Templates) {
          message.error("Failed to import template");
        } else if (menuType === ListType.Coa) {
          message.error("Failed to import CoA");
        }
        return;
      }
    }
  };

  const handleExport = async () => {
    let fileName = "example.xlsx";
    try {
      if (menuType === ListType.Templates) {
        fileName = `${searchParams.get(NAME_KEY)}.xlsx`;
        await onHandleTemplateExportClick(deliverableId, fileName);
      } else if (menuType === ListType.Coa) {
        fileName = `${searchParams.get(NAME_KEY)}.xlsx`;
        await onHandleCoaExportClick(deliverableId, fileName);
      }
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
      return;
    }
  };

  //Edit
  const handleAgGridAddRowClick = () => {
    setCount(0);
    setShowAddNewRow(true);
  };

  const handleCancelClick = () => {
    if (agGridRef.current) {
      agGridRef.current.cancel();
    }
    setIsEditMode(false);
  };
  //view
  const handleEditOnlineClick = () => {
    setIsEditMode(true);
    if (agGridRef.current) {
      agGridRef.current.editOnline();
    }
  };

  return (
    <StyledLinkAnimation hovercolor={ThemePalette.colorWhite}>
      <StyledProjectMenu isEditMode={isEditMode}>
        <section className="top-header">
          <img
            className="image-logo"
            onClick={() => {
              setAdditionalTabsVisible(false);
              navigate("/deliverable");
            }}
            src="/PropelLogo.svg"
            alt="Logo"
          />

          <div
            className="menu-container"
            onClick={() => {
              if (menuType === ListType.Templates) {
                navigate(`/template`);
              } else if (menuType === ListType.Coa) {
                navigate(`/coa`);
              }
            }}
          >
            <ArrowLeftOutlined className="custom-arrow-icon" />
            <div className="project-heading">{templateName ?? null}</div>
          </div>
        </section>
        <section className="top-header-right">
          {additionalTabsVisible && (
            <AdditionalTabs>
              {/* <VersionTab>
                <span>v1.7</span>
              </VersionTab> */}
              {isEditMode ? (
                <>
                  <AddRowTab
                    disabled={!hasImportTemplatePermission}
                    className="nav-items"
                    onClick={handleAgGridAddRowClick}
                  >
                    {ADDROW_TEXT}
                  </AddRowTab>
                  <SaveTab
                    disabled={!hasExportTemplatePermission()}
                    className="nav-items"
                    onClick={onSaveClick}
                  >
                    <span>{SAVE_TEXT}</span>
                  </SaveTab>
                  <CancelTab
                    disabled={false}
                    className="nav-items"
                    onClick={handleCancelClick}
                  >
                    {CANCEL_TEXT}
                  </CancelTab>
                </>
              ) : (
                <>
                  <EditOnlineTab
                    disabled={!hasImportTemplatePermission}
                    className="nav-items"
                    onClick={handleEditOnlineClick}
                  >
                    {EDIT_ONLINE_TEXT}
                  </EditOnlineTab>
                  <ImportTab
                    disabled={!hasImportTemplatePermission()}
                    className="nav-items"
                  >
                    <label className="custom-file-upload">
                      <input
                        type="file"
                        title=" "
                        name={IMPORT_BUTTON_LABEL.toLowerCase()}
                        id={IMPORT_BUTTON_LABEL.toLowerCase()}
                        onChange={async (e) => {
                          handleImportButtonClick(e);
                          e.target.value = "";
                        }}
                        accept=".xls, .xlsx"
                        disabled={!hasExportTemplatePermission()}
                      />
                      {IMPORT_BUTTON_LABEL}
                    </label>
                  </ImportTab>

                  <ExportTab
                    onClick={handleExport}
                    disabled={!hasExportTemplatePermission()}
                    className="nav-items"
                  >
                    <span>{EXPORT_BUTTON_LABEL}</span>
                  </ExportTab>
                </>
              )}
            </AdditionalTabs>
          )}
          <UserProfile />
        </section>
      </StyledProjectMenu>
    </StyledLinkAnimation>
  );
};
